import { useQuery } from "@tanstack/react-query";
import { getAttribution } from "@/lib/referral";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, Lock, Check, ArrowLeft, ArrowRight, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Seo } from "@/components/site/Section";
import { OrderSummary } from "@/components/checkout/OrderSummary";
import { WaitlistForm } from "@/components/site/WaitlistForm";
import { usePlans, useOffer, http, errorMessage, formatGBP } from "@/lib/api";
import { isValidPostcode } from "@/lib/postcode";
import { cn } from "@/lib/utils";

const STEPS = ["Postcode", "Plan", "Your details", "Delivery", "Review"];
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;
const PHONE = /^\+?[0-9 ()-]{10,20}$/;

const validateStep = (step, f) => {
  const e = {};
  if (step === 0 && !isValidPostcode(f.postcode)) e.postcode = "Please enter a full UK postcode, e.g. BT39 0AA.";
  if (step === 2) {
    if (f.full_name.trim().length < 2) e.full_name = "Please enter your full name.";
    if (!EMAIL.test(f.email)) e.email = "Please enter a valid email address.";
    if (!PHONE.test(f.phone.trim())) e.phone = "Please enter a valid UK telephone number.";
    if (f.password.length < 8) e.password = "Please choose a password of at least 8 characters.";
    else if (f.password !== f.password2) e.password2 = "Passwords don't match.";
  }
  if (step === 3) {
    if (f.address_line1.trim().length < 3) e.address_line1 = "Please enter the first line of your address.";
    if (f.town.trim().length < 2) e.town = "Please enter your town.";
    if (!isValidPostcode(f.postcode)) e.postcode = "Please enter a full UK postcode, e.g. BT39 0AA.";
  }
  if (step === 4 && !f.accept_terms) e.accept_terms = "Please accept the terms and subscription policy to continue.";
  return e;
};

const Field = ({ id, label, error, children }) => (
  <div className="space-y-2">
    <Label htmlFor={id} className="text-sm text-[#F7F5F0]/80">{label}</Label>
    {children}
    {error && <p id={`${id}-error`} className="text-sm text-[#ff8a3d]" data-testid={`${id}-error`}>{error}</p>}
  </div>
);

const Stepper = ({ step }) => (
  <ol className="flex gap-2" aria-label="Checkout progress">
    {STEPS.map((s, i) => (
      <li key={s} className="flex-1" aria-current={i === step ? "step" : undefined}>
        <span className={cn("block h-1 rounded-full transition-colors", i <= step ? "bg-[#FF6A00]" : "bg-[#262626]")} />
        <span className={cn("mt-2 hidden text-xs sm:block", i === step ? "text-[#F7F5F0]" : "text-[#6f6f6f]")}>{s}</span>
      </li>
    ))}
  </ol>
);

export default function Checkout() {
  const [params] = useSearchParams();
  const { data: plans, isLoading } = usePlans();
  const { data: offer } = useOffer();
  const code = params.get("code");
  const { data: custom, isLoading: customLoading, isError: customError } = useQuery({
    queryKey: ["custom", code], enabled: !!code, retry: false,
    queryFn: async () => (await http.get(`/custom-packages/${encodeURIComponent(code)}`)).data,
  });
  const plansList = code ? (custom ? [custom] : []) : plans;
  const draft = params.get("resume") ? JSON.parse(sessionStorage.getItem("wlr_checkout_draft") || "null") : null;
  const [step, setStep] = useState(draft ? 2 : 0);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [zone, setZone] = useState(null);
  const [f, setF] = useState(draft ? { ...draft, plan: params.get("plan") || draft.plan, password: "", password2: "", accept_terms: false } : {
    plan: params.get("plan") || "regular", full_name: "", email: "", phone: "", password: "", password2: "", address_line1: "", address_line2: "",
    town: "", postcode: sessionStorage.getItem("lc_postcode") || "", instructions: "", accept_terms: false,
  });
  const plan = useMemo(() => (code ? custom : plans?.find((p) => p.slug === f.plan)), [code, custom, plans, f.plan]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setPostcode = (e) => {
    setF({ ...f, postcode: e.target.value.toUpperCase() });
    setZone(null);
    setErrors(({ postcode, ...rest }) => rest);
  };
  const inp = (k, extra = {}) => ({
    id: `co-${k}`, value: f[k], onChange: set(k), "aria-invalid": !!errors[k], "aria-describedby": errors[k] ? `co-${k}-error` : undefined,
    className: "lc-input", "data-testid": `checkout-${k}-input`, ...extra,
  });

  const next = async () => {
    const e = validateStep(step, f);
    setErrors(e);
    if (Object.keys(e).length) return;
    if (step === 0 || step === 3) {
      setBusy(true);
      try {
        const { data } = await http.post("/postcode/check", { postcode: f.postcode });
        if (!data.eligible) {
          setErrors({ postcode: "Sorry, we're not delivering to your postcode just yet." });
          setZone({ ineligible: true, postcode: data.postcode });
          return;
        }
        setZone(data);
        setF((x) => ({ ...x, postcode: data.postcode }));
      } catch (err) {
        setErrors({ postcode: errorMessage(err) });
        return;
      } finally {
        setBusy(false);
      }
    }
    setStep(step + 1);
  };

  const submit = async () => {
    const e = validateStep(4, f);
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    setNotice(null);
    try {
      const { password2, ...payload } = f;
      const { data } = await http.post("/checkout/session", { ...payload, plan: plan.slug, custom_code: code || null, origin_url: window.location.origin, attribution: getAttribution() });
      if (data.url) {
        const { accept_terms, password, ...rest } = payload;
        sessionStorage.setItem("wlr_checkout_draft", JSON.stringify(rest));
        window.location.href = data.url;
      }
    } catch (err) {
      setNotice({ type: err?.response?.status === 503 ? "info" : "error", text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  };

  if (isLoading || (code && customLoading)) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[#FF6A00]" /></div>;

  return (
    <div className="lc-container pt-28 pb-20" data-testid="checkout-page">
      <Seo title="Checkout" noindex />
      <Link to="/plans" className="inline-flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-[#F7F5F0]" data-testid="checkout-back-to-plans"><ArrowLeft className="h-4 w-4" /> Back to plans</Link>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold tracking-tight">Join WeeLogRun</h1>
      <p className="mt-2 text-[#A1A1AA]">Takes about two minutes. You'll create a password so you can manage your subscription any time.</p>
      <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-7">
          <Stepper step={step} />
          <div className="mt-8 rounded-3xl border border-[#262626] bg-[#101010] p-6 sm:p-8">
            {code && customError && step === 0 && <p className="mb-6 rounded-2xl bg-[#ff8a3d]/10 p-4 text-sm text-[#ff8a3d]" data-testid="checkout-custom-error-top">That code isn't valid or has already been used. <Link to="/plans" className="underline">Back to plans</Link> or get in touch.</p>}
            {step === 0 && (
              <div className="space-y-5" data-testid="checkout-step-postcode">
                <h2 className="font-display text-2xl font-bold">Do we deliver to you?</h2>
                <p className="text-sm text-[#A1A1AA]">Enter your full postcode first so you know straight away. We currently deliver to BT36, BT37, BT38 and BT39.</p>
                <Field id="co-postcode" label="Your postcode" error={errors.postcode}>
                  <Input {...inp("postcode", { autoComplete: "postal-code", className: "lc-input uppercase", placeholder: "e.g. BT39 0AA",
                    onChange: setPostcode,
                    onKeyDown: (e) => e.key === "Enter" && next() })} />
                </Field>
                {zone?.ineligible && (
                  <div className="rounded-2xl border border-[#2E2E2E] bg-[#0a0a0a] p-5" data-testid="checkout-postcode-ineligible">
                    <p className="text-sm text-[#F7F5F0]/85">Join the waiting list and we'll email you as soon as we reach {zone.postcode}.</p>
                    <WaitlistForm postcode={zone.postcode} />
                  </div>
                )}
              </div>
            )}
            {step === 1 && (
              <fieldset className="space-y-3" data-testid="checkout-step-plan">
                <legend className="mb-4 font-display text-2xl font-bold">{code ? "Your custom package" : "Choose your plan"}</legend>
                {code && customError && <p className="text-sm text-[#ff8a3d]" data-testid="checkout-custom-error">That code isn't valid or has already been used. <Link to="/plans" className="underline">Back to plans</Link></p>}
                {plansList.map((p) => (
                  <button key={p.slug} type="button" onClick={() => setF({ ...f, plan: p.slug })} aria-pressed={plan?.slug === p.slug}
                    className={cn("flex w-full items-center justify-between gap-4 rounded-2xl border p-5 text-left transition-colors", plan?.slug === p.slug ? "border-[#FF6A00] bg-[#FF6A00]/10" : "border-[#2E2E2E] hover:border-[#444]")}
                    data-testid={`checkout-plan-${p.slug}`}>
                    <span>
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1 font-display text-xl font-bold">{p.name}{p.popular && <span className="inline-flex shrink-0 whitespace-nowrap rounded-full bg-[#D4A437] px-2.5 py-0.5 font-sans text-[10px] font-bold uppercase leading-4 tracking-wider text-[#080808]" data-testid="checkout-popular-badge">Most popular</span>}</span>
                      <span className="mt-1 block text-sm text-[#A1A1AA]">{(code ? p.contents : p.contents.slice(0, 3)).join(" · ")}</span>
                    </span>
                    <span className="flex items-center gap-3">
                      <span className="font-display text-xl font-bold">{formatGBP(p.price_pence)}</span>
                      <span className={cn("grid h-6 w-6 place-items-center rounded-full border", plan?.slug === p.slug ? "border-[#FF6A00] bg-[#FF6A00] text-[#080808]" : "border-[#444]")}>{plan?.slug === p.slug && <Check className="h-4 w-4" />}</span>
                    </span>
                  </button>
                ))}
              </fieldset>
            )}
            {step === 2 && (
              <fieldset className="space-y-5" data-testid="checkout-step-details">
                <legend className="mb-4 font-display text-2xl font-bold">Your details</legend>
                <Field id="co-full_name" label="Full name" error={errors.full_name}><Input {...inp("full_name", { autoComplete: "name" })} /></Field>
                <Field id="co-email" label="Email address" error={errors.email}><Input {...inp("email", { type: "email", autoComplete: "email" })} /></Field>
                <Field id="co-phone" label="Telephone number" error={errors.phone}><Input {...inp("phone", { type: "tel", autoComplete: "tel" })} /></Field>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field id="co-password" label="Create a password" error={errors.password}><Input {...inp("password", { type: "password", autoComplete: "new-password" })} /></Field>
                  <Field id="co-password2" label="Confirm password" error={errors.password2}><Input {...inp("password2", { type: "password", autoComplete: "new-password" })} /></Field>
                </div>
                <p className="text-xs text-[#8a8a8a]">At least 8 characters. You'll use your email and this password to sign in to My WeeLogRun.</p>
              </fieldset>
            )}
            {step === 3 && (
              <fieldset className="space-y-5" data-testid="checkout-step-delivery">
                <legend className="mb-4 font-display text-2xl font-bold">Delivery address</legend>
                <Field id="co-address_line1" label="Address line 1" error={errors.address_line1}><Input {...inp("address_line1", { autoComplete: "address-line1" })} /></Field>
                <Field id="co-address_line2" label="Address line 2 (optional)"><Input {...inp("address_line2", { autoComplete: "address-line2" })} /></Field>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field id="co-town" label="Town" error={errors.town}><Input {...inp("town", { autoComplete: "address-level2" })} /></Field>
                  <Field id="co-postcode" label="Postcode" error={errors.postcode}><Input {...inp("postcode", { autoComplete: "postal-code", className: "lc-input uppercase", onChange: setPostcode })} /></Field>
                </div>
                {zone?.ineligible && <Link to="/delivery-areas#check" className="inline-block text-sm text-[#FF6A00] underline" data-testid="checkout-waitlist-link">Join the waiting list for your area</Link>}
                <Field id="co-instructions" label="Delivery instructions (optional)">
                  <Textarea id="co-instructions" value={f.instructions} onChange={set("instructions")} maxLength={500} placeholder="e.g. Please leave beside the side gate" className="rounded-xl border-[#2E2E2E] bg-[#0f0f0f] text-base" data-testid="checkout-instructions-input" />
                </Field>
              </fieldset>
            )}
            {step === 4 && (
              <div className="space-y-5" data-testid="checkout-step-review">
                <h2 className="font-display text-2xl font-bold">Review & confirm</h2>
                <dl className="grid gap-3 text-sm sm:grid-cols-2">
                  <div className="rounded-xl bg-[#0a0a0a] p-4"><dt className="text-[#8a8a8a]">Name</dt><dd className="mt-1 font-medium">{f.full_name}</dd><dd className="text-[#A1A1AA]">{f.email} · {f.phone}</dd></div>
                  <div className="rounded-xl bg-[#0a0a0a] p-4"><dt className="text-[#8a8a8a]">Deliver to</dt><dd className="mt-1 font-medium">{f.address_line1}{f.address_line2 && `, ${f.address_line2}`}, {f.town} {f.postcode}</dd>{f.instructions && <dd className="text-[#A1A1AA]">{f.instructions}</dd>}</div>
                </dl>
                <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-[#262626] p-4 text-sm" htmlFor="co-terms">
                  <Checkbox id="co-terms" checked={f.accept_terms} onCheckedChange={(v) => setF({ ...f, accept_terms: !!v })} className="mt-0.5 border-[#555] data-[state=checked]:bg-[#FF6A00] data-[state=checked]:border-[#FF6A00]" data-testid="checkout-terms-checkbox" />
                  <span className="text-[#F7F5F0]/85">I agree to the <Link to="/terms" className="text-[#FF6A00] underline" target="_blank">Terms and Conditions</Link> and <Link to="/subscription-policy" className="text-[#FF6A00] underline" target="_blank">Subscription & Cancellation Policy</Link>, and understand my subscription renews monthly until I cancel. I agree that Kingsmoss Logs (WeeLogRun) may charge my card through our payment provider, Stripe, {plan ? formatGBP(plan.price_pence) : "the plan price"} each month on the same date, starting today, until I pause or cancel.</span>
                </label>
                {errors.accept_terms && <p className="text-sm text-[#ff8a3d]" data-testid="co-terms-error">{errors.accept_terms}</p>}
                {notice && (
                  <div className={cn("flex gap-3 rounded-2xl border p-4 text-sm", notice.type === "info" ? "border-[#D4A437]/40 bg-[#D4A437]/10 text-[#F3E0A2]" : "border-[#ff8a3d]/40 bg-[#ff8a3d]/10 text-[#ffb27a]")} role="alert" data-testid="checkout-notice">
                    <AlertCircle className="h-5 w-5 shrink-0" /> {notice.text}
                  </div>
                )}
              </div>
            )}
            <div className="mt-8 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between">
              {step > 0 ? <button type="button" className="lc-btn-ghost" onClick={() => setStep(step - 1)} data-testid="checkout-prev">Back</button> : <span />}
              {step < 4 ? (
                <button type="button" className="lc-btn-primary" onClick={next} disabled={busy || !plan} data-testid="checkout-next">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Continue <ArrowRight className="h-4 w-4" /></>}
                </button>
              ) : (
                <button type="button" className="lc-btn-primary" onClick={submit} disabled={busy} data-testid="checkout-submit">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4" /> Continue to secure payment</>}
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="lg:col-span-5">
          <OrderSummary plan={plan} offer={offer} chargePence={zone?.delivery_charge_pence || 0} />
        </div>
      </div>
    </div>
  );
}
